import { useContext, useEffect, useRef, useState } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { useMediaQuery } from "react-responsive";
import { JobsCount } from "../components/Search/JobsCount";
import { LongJobCardListLoader } from '../components/Loaders/LongJobCardListLoader';
import { LongJobCard } from '../components/Shared/LongJobCard';
import React from 'react';
import { Pagination } from '../components/Shared/Pagination';
import { Job } from '../models/Job';
import { SearchCriteria } from '../models/SearchCriteria';
import { GetIntegrationSectorsAndJobs, getSearchJobs } from '../services/JobService';
import useErrorPage from '../hooks/useErrorPage';
import { useParams } from "react-router-dom";
import { Sector } from "../models/Sector";

const pageSize = 10;
const maxRetries = 3;

export const IntegrationJobsPage: React.FC = () => {
    const { integrator = "" } = useParams<{ integrator: string }>();
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("integrationJobsPage"));         
    const [jobs, setJobs] = useState<Job[]>([]);
    const [sectors, setSectors] = useState<Sector[]>();
    const [selectedSector, setSelectedSector] = useState<string>('');
    const [recordCount, setRecordCount] = useState<number>();
    const [page, setPage] = useState<number>(1);    
    const [totalPages, setTotalPages] = useState<number>();
    const [keyWord, setKeyword] = useState<string>(''); 
    const [logoUrl, setLogoUrl] = useState<string>();
    const [description, setDescription] = useState<string>();   
    const isMountedRef = useRef<boolean>(true);
    const retryCountRef = useRef<number>(1); 
    const scrollElementRef = useRef<any>();
    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [loading, setLoading] = useState<boolean>(true); 
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();

    useEffect(() => {         
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {         
        setStyles(styleContext.getComponentStyle("integrationJobsPage"));
    }, [isMobile]);

    useEffect(() => {
        if(integrator) {
           GetIntegrationData();
        }
    }, [integrator])

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // Set 1000ms debounce time
        debounceTimeout.current = setTimeout(() => {
            getJobList();
        }, 1000);
        
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };                              
    }, [page, selectedSector, keyWord]);

    async function getJobList() {
        // Cancel any in-progress requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        // Create a new AbortController for the current request
        const controller = new AbortController();
        abortControllerRef.current = controller;       
        setLoading(true);
        let searchCriteria: SearchCriteria = {
            page: page,
            pageSize: pageSize,
            sectors: selectedSector,
            keyword: keyWord
        }
        await getSearchJobs(searchCriteria)
        .then((jobList: Job[]) => {
            setJobs(jobList); 
            if(jobList.length > 0) {
                let totalPageCount = Math.ceil(jobList[0].recordCount / pageSize);            
                setTotalPages(totalPageCount);
                setRecordCount(jobList[0].recordCount);
            } else {
                setTotalPages(1);
                setRecordCount(0);
            }
            setLoading(false);
        })    
        .catch((error) => {            
            if(isMountedRef.current) {                   
                if (retryCountRef.current < maxRetries) {
                    // Retry the API call after 1 second
                    retryCountRef.current = retryCountRef.current + 1;
                    setTimeout(getJobList, 1000);
                } 
                else {
                    // Redirect to error page after max retries
                    toErrorPage(error);
                }  
            }
        });       
    }

    async function GetIntegrationData() {
        await GetIntegrationSectorsAndJobs(integrator)
        .then((data: { sectors: Sector[]; jobs: Job[]; logoUrl: string; description: string}) => {        
            setSectors(data.sectors);
            setLogoUrl(data.logoUrl);
            setDescription(data.description);
            const firstSectorId = data.sectors[0].sectorId.toString();
            setSelectedSector(firstSectorId);
            setJobs(data.jobs);
            if (data.jobs.length > 0) {
                setRecordCount(data.jobs[0].recordCount);
                setTotalPages(Math.ceil(data.jobs[0].recordCount / pageSize));
            }           
            setLoading(false);
        })
        .catch((error) => {
            toErrorPage(error);
        });
    }

    const handlePagination = (page: number) => {        
        setPage(page)
        scrollElementRef.current.scrollIntoView();       
    }

    const handleSectorTabClick = (sectorId: number) => {        
        setPage(1);
        setSelectedSector(sectorId.toString());        
    }

    const handleJobCardClick = (event: React.MouseEvent, jobId: number) => {
        event.stopPropagation();
        event.preventDefault();
        window.open(`https://xpress.jobs/jobs/view/${jobId}`, '_blank')
    }

    const handleChange =(event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
        setPage(1);
    }

    return (
        <>
            {(logoUrl || description) && (
                <div style={styles.headerContainer}>
                    {logoUrl && (
                        <div style={styles.logoBox}>
                            <img src={logoUrl} alt="integrator logo" style={styles.logoImage} />
                        </div>
                    )}
                    {description && (
                        <div style={styles.descriptionBox}>
                            <p style={styles.descriptionText}>{description}</p>
                        </div>
                    )}
                </div>
            )} 
            <div style={styles.baseWrapper} ref={scrollElementRef}>
                {sectors && (
                    <div style={styles.sectorTabsWrapper}>
                        {sectors.map((sectorObj: Sector, index: number) => (
                            <div 
                                key={index}
                                onClick={() => handleSectorTabClick(sectorObj.sectorId)} 
                                style={{...styles.sectorTab, ...(selectedSector === sectorObj.sectorId.toString() && styles.sectorTab.selected)}}
                            >
                                {sectorObj.sectorName?.trim()}
                            </div>
                        ))}
                    </div>
                )}
                <div style={styles.searchInputWrapper}>
                    <input
                        type="text"
                        style={styles.searchInput}
                        value={keyWord}
                        onChange={handleChange}
                        placeholder="Search Jobs ..." 
                    />
                </div>                       
                {<JobsCount styles={styles.jobsCount} jobCount={recordCount!} isLoading={loading} jobCountTitle={"Jobs Found"} sectorIds={selectedSector}/>}
                {loading ? <LongJobCardListLoader/> :
                    <>                                
                        {jobs.length > 0 && <>
                            {jobs.map((job, index) =>
                                <div key={index} onClickCapture={(e) => handleJobCardClick(e, job.jobId)}>
                                    <LongJobCard styles={styles.card} job={job} isLastItem={jobs.length - 1 === index} />
                                </div>
                            )}
                            {(totalPages! > 1) && <Pagination page={page} totalPages={totalPages!} handlePagination={handlePagination} extendedStyles={styles.pagination} />}
                        </>}
                    </>
                }
                <div style={styles.logoWrapper}>
                    <img style={styles.poweredByLogo} src={process.env.PUBLIC_URL + "/img/powered_by_logo.png"} alt="powered by xpressJobs logo" />        
                </div>
            </div>
        </>     
    );
}